// Card 54, the epicentre (R803, ui-map-interface.md §17.3.54).
//
// A seismic chart marks an epicentre with a star and rings the ground the
// shock reached. Here the mark stands where the NEXT seaquake shock
// strikes: the quake holds one trench at a time, strikes at its epicentre
// cell, and after every shock moves to a different trench (numbers.md
// §15). The ring is the shock's reach, and every fleet inside it takes the
// flat hull share the label states, so a commander sees which water the
// next shock takes and how long she has to leave it.
//
// The piece reads `state.quake`, the last QuakeInfo off the wire. Every
// commander receives the same frame, because the seaquake is on the world
// clock and is no secret. It draws nothing at stage 0, and nothing while
// the quake holds no trench.
//
// **IT STAYS CHEAP IN THE SETTLEMENT.** There the quake moves every few
// seconds and the ring grows past the board. A pack is one dashed ring
// (ONE circle instance at any radius), eight star segments and two short
// runs of glyphs, so a move costs the same as a stage change.

import { chart as S } from '../../../gen/strings';
import { polylineLength, type Pt } from '../../../render/text-run';
import { mpxToPx } from '../../../world';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import {
  CHART_INK, dashedRing, faceOf, hourOfDay, inkOf, onTheHour, seasonDay,
} from './shared';
import { leftSpan, leftTicksBySecond } from '../../../locale/span';

/** The mark of the last pack, in world px. */
interface Mark {
  x: number;
  y: number;
  /** The reach of the next shock. */
  r: number;
}

export class EpicentrePiece implements Piece {
  readonly id = 'epicentre' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'static' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = ['quake', 'minute', 'reading', 'hello', 'font'] as const;

  /** The mark, or null when nothing is drawn. The tests read it. */
  mark: Mark | null = null;
  /** The label under the star. */
  label = '';
  /** The words along the ring. */
  private ringText = '';
  /** The upper half of the ring, left to right, for the words. */
  private path: Pt[] = [];
  /** Where along `path` the words are centred. */
  private pathMid = 0;
  /** The modern label counts under an hour, so it moves each second. */
  private bySecond = false;
  /** The world second of the last build. */
  private second = -1;

  visible(ctx: ChartContext): boolean {
    const q = ctx.state.quake;
    return q !== null && q.stage > 0 && q.placed;
  }

  rebuild(ctx: ChartContext): void {
    this.mark = null;
    this.label = '';
    this.ringText = '';
    this.path = [];
    this.bySecond = false;
    this.second = Math.floor(ctx.worldClockS);
    const q = ctx.state.quake;
    if (q === null || q.stage === 0 || !q.placed) return;
    const fig = ctx.cfg.pieces.epicentre.fig;
    const x = mpxToPx(q.xMpx);
    const y = mpxToPx(q.yMpx);
    const r = q.radiusPx;
    const next = ctx.state.quakeNextSec();
    const pct = Math.round(q.damagePctX10 / 10);

    if (ctx.reading === 'historical') {
      // The surveyor dates the next shock: the season day and the hour it
      // lands on, on the world clock.
      this.label = S.quake_historical({
        day: seasonDay(ctx.state, next),
        hour: onTheHour(hourOfDay(ctx.worldClockS + next)),
      });
      this.ringText = S.quake_ring_historical({ pct });
    } else {
      // The countdown writes its units (R2741): `3h 23m`, or `3m 23s`
      // under an hour, which moves each second.
      this.label = S.quake_modern({
        stage: q.stage, px: r, pct, next: leftSpan(next),
      });
      this.bySecond = leftTicksBySecond(next);
      this.ringText = S.quake_ring_modern({ px: r });
    }

    // The words ride the upper half of the ring, just outside it, from the
    // left over the top to the right, so they read upright.
    const rr = r + fig.ring_text_gap;
    const n = Math.max(2, Math.round(fig.ring_samples));
    for (let i = 0; i <= n; i++) {
      const a = Math.PI + (Math.PI * i) / n;
      this.path.push({ x: x + rr * Math.cos(a), y: y + rr * Math.sin(a) });
    }
    this.pathMid = polylineLength(this.path, false) / 2;
    this.mark = { x, y, r };
  }

  /** A new world second while the label counts under an hour (R2741). */
  outgrown(ctx: ChartContext): boolean {
    return this.bySecond && Math.floor(ctx.worldClockS) !== this.second;
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    // The static pack culls nothing: it outlives every pan (R2238).
    const m = this.mark;
    if (m === null) return;
    const t = ctx.cfg.pieces.epicentre;
    const fig = t.fig;
    const r = t[ctx.reading];
    const ink = inkOf(r.ink, CHART_INK.gold);
    const face = faceOf(ctx.reading);

    dashedRing(sinks.circles, m.x, m.y, m.r, r.width, r.dash, ink, r.alpha);

    // The star: eight points, long and short in turn, closed.
    let px = 0;
    let py = 0;
    for (let k = 0; k <= 8; k++) {
      const a = ((k % 8) * Math.PI) / 4;
      const len = k % 2 === 1 ? fig.star_r_short : fig.star_r_long;
      const qx = m.x + Math.cos(a) * len;
      const qy = m.y + Math.sin(a) * len;
      if (k > 0) sinks.segments.line(px, py, qx, qy, fig.star_width, ink, fig.star_alpha);
      px = qx;
      py = qy;
    }

    ctx.glyphs.word(
      sinks.quads, this.label, m.x, m.y + fig.label_dy,
      face, t.text.main, ink, r.fig.label_alpha, { anchor: 'centre' },
    );
    ctx.glyphs.wordAlong(
      sinks.quads, this.ringText, this.path,
      face, t.text.main, ink, r.fig.label_alpha,
      { anchor: 'centre', startS: this.pathMid },
    );
  }
}

/** The registry's entry. */
export const epicentre = new EpicentrePiece();
